
import { supabase } from '@/integrations/supabase/client';
import { Integration } from '@/types/integrations';

export const createWhatsAppIntegrationService = (
  fetchIntegrations: () => Promise<void>,
  toast: any
) => {
  const createIntegration = async (type: 'whatsapp' | 'nuvemshop', config: any) => {
    try {
      console.log('Creating integration:', type, config);
      
      // Para Nuvemshop, não criar integração aqui - será criada via OAuth
      if (type === 'nuvemshop') {
        throw new Error('Use o botão "Conectar com Nuvemshop" para integrar via OAuth');
      }
      
      // Verificar se já existe uma integração do tipo WhatsApp
      const { data: existingIntegrations } = await supabase
        .from('integrations')
        .select('*')
        .eq('integration_type', type);

      if (existingIntegrations && existingIntegrations.length > 0) {
        console.log('Integration already exists:', existingIntegrations[0]);
        toast({
          title: "Integração já existe",
          description: `A integração ${type} já está configurada.`,
        });
        // Type assertion for existing integration
        return {
          ...existingIntegrations[0],
          integration_type: existingIntegrations[0].integration_type as 'whatsapp' | 'nuvemshop'
        } as Integration;
      }

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Usuário não autenticado');
      }

      const { data, error } = await supabase
        .from('integrations')
        .insert({
          user_id: user.id,
          integration_type: type,
          status: 'configuring',
          config: config,
          connection_status: 'disconnected'
        })
        .select()
        .single();

      if (error) throw error;

      toast({
        title: "Integração criada",
        description: `Integração ${type} criada com sucesso.`,
      });

      await fetchIntegrations();
      
      // Type assertion for new integration
      return {
        ...data,
        integration_type: data.integration_type as 'whatsapp' | 'nuvemshop'
      } as Integration;
    } catch (error: any) {
      console.error('Error creating integration:', error);
      toast({
        title: "Erro ao criar integração",
        description: error.message,
        variant: "destructive",
      });
      throw error;
    }
  };

  const testConnection = async (integration: Integration) => {
    try {
      console.log('Testing connection for integration:', integration.id);
      
      const { data, error } = await supabase.functions.invoke('uazapi-integration', {
        body: {
          action: 'test_connection',
          integrationId: integration.id
        }
      });

      if (error) throw error;

      console.log('Test connection result:', data);

      if (data.success) {
        toast({
          title: "Teste de conexão",
          description: "Conexão testada com sucesso!",
        });
      } else {
        throw new Error(data.error || 'Falha no teste de conexão');
      }

      return true;
    } catch (error: any) {
      console.error('Error testing connection:', error);
      toast({
        title: "Erro no teste de conexão",
        description: error.message,
        variant: "destructive",
      });
      return false;
    }
  };

  const generateQRCode = async (integration: Integration) => {
    try {
      console.log('Generating QR code for integration:', integration.id);
      
      const { data, error } = await supabase.functions.invoke('uazapi-integration', {
        body: {
          action: 'generate_qr_code',
          integrationId: integration.id
        }
      });

      if (error) {
        console.error('Supabase function error:', error);
        throw new Error(`Erro na função: ${error.message}`);
      }

      console.log('QR code generation result:', data);

      if (data.success) {
        await fetchIntegrations();
        return data.qr_code_url;
      } else {
        throw new Error(data.error || 'Falha ao gerar QR Code');
      }
    } catch (error: any) {
      console.error('Error generating QR code:', error);
      
      // Melhor tratamento de erro com informações mais específicas
      let errorMessage = 'Erro desconhecido ao gerar QR Code';
      
      if (error.message?.includes('JSON')) {
        errorMessage = 'Erro de formato na resposta da API. Verifique a configuração do token.';
      } else if (error.message?.includes('Token')) {
        errorMessage = 'Token da Uazapi inválido ou expirado.';
      } else if (error.message?.includes('Network')) {
        errorMessage = 'Erro de conectividade. Verifique sua conexão.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Erro ao gerar QR Code",
        description: errorMessage,
        variant: "destructive",
      });
      
      throw error;
    }
  };

  const checkConnectionStatus = async (integration: Integration) => {
    try {
      console.log('Checking connection status for integration:', integration.id);
      
      const { data, error } = await supabase.functions.invoke('uazapi-integration', {
        body: {
          action: 'check_status',
          integrationId: integration.id
        }
      });

      if (error) throw error;

      console.log('Connection status result:', data);

      if (data.success) {
        await fetchIntegrations();
        return data.status;
      } else {
        console.error('Status check failed:', data.error);
        return 'error';
      }
    } catch (error: any) {
      console.error('Error checking connection status:', error);
      return 'error';
    }
  };

  return {
    createIntegration,
    testConnection,
    generateQRCode,
    checkConnectionStatus
  };
};
